import React, { useEffect, useState } from "react";
import { format, getDate } from "date-fns";
import { he } from "date-fns/locale";
import { ChevronLeft, ChevronRight, CalendarClock } from "lucide-react";
import { useCalendar } from "../hook/useCalendar";
import { useGetMeetingsByMonthQuery } from "../../../api/calendarApi";
import DailyMeetingsSidebar from "./DailyMeetingsSidebar";
import { useSelector } from "react-redux";
import { selectCurrentUser } from "../../../store/slices/authSlice";

const WEEKDAYS_HEBREW = ["א", "ב", "ג", "ד", "ה", "ו", "ש"];

const Calendar = () => {
  const { currentDate, daysInCalendar, goToNextMonth, goToPrevMonth } =
  useCalendar();
  const currentUser = useSelector(selectCurrentUser);
  const mentorId = currentUser?.id || "";

  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedMeetings, setSelectedMeetings] = useState<any[]>([]);

  const monthStr = format(currentDate, "yyyy-MM");
  const { data: monthMeetings = [] ,refetch} = useGetMeetingsByMonthQuery({
    mentorId,
    month: monthStr,
  });

  const handleDayClick = (date: Date) => {
    const meetingsForDay = monthMeetings.filter((meeting) =>
      meeting.date.startsWith(format(date, "yyyy-MM-dd"))
    );
    setSelectedDate(date);
    setSelectedMeetings(meetingsForDay);
  };

  const closeSidebar = () => {
    setSelectedDate(null);
    setSelectedMeetings([]);
  };

  useEffect(() => {
    if (selectedDate) {
      const meetingsForDay = monthMeetings.filter((meeting) =>
        meeting.date.startsWith(format(selectedDate, "yyyy-MM-dd"))
      );
      setSelectedMeetings(meetingsForDay);
    }
  }, [monthMeetings, selectedDate]);

  return (
    <div className="w-full max-w-[850px] flex flex-col mx-auto gap-6">
      <div className="flex-1 bg-secondary shadow-card rounded-2xl overflow-hidden border border-border">
        <div className="bg-gradient-to-r from-primary to-highlight p-6">
          <div className="flex items-center justify-between">
            <button
              onClick={goToPrevMonth}
              className="p-2 rounded-xl bg-secondary/20 hover:bg-secondary/30 transition-all duration-200 backdrop-blur-sm"
            >
              <ChevronRight className="w-6 h-6 text-secondary" />
            </button>
            <h2 className="text-3xl font-bold text-secondary text-center">
              {format(currentDate, "MMMM yyyy", { locale: he })}
            </h2>
            <button
              onClick={goToNextMonth}
              className="p-2 rounded-xl bg-secondary/20 hover:bg-secondary/30 transition-all duration-200 backdrop-blur-sm"
            >
              <ChevronLeft className="w-6 h-6 text-secondary" />
            </button>
          </div>
        </div>

        <div className="grid grid-cols-7 bg-muted border-b border-border">
          {WEEKDAYS_HEBREW.map((day, index) => (
            <div
              key={index}
              className="text-center py-4 font-semibold text-text-main bg-muted"
            >
              <span className="text-lg">{day}</span>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7">
          {daysInCalendar.map(({ date, isCurrentMonth }, index) => {
            const meetingsForDay = monthMeetings.filter((meeting) =>
              meeting.date.startsWith(format(date, "yyyy-MM-dd"))
            );
            const isToday =
              format(date, "yyyy-MM-dd") === format(new Date(), "yyyy-MM-dd");
            const isSelected =
              selectedDate &&
              format(date, "yyyy-MM-dd") === format(selectedDate, "yyyy-MM-dd");

            return (
              <div
                key={index}
                onClick={() => isCurrentMonth && handleDayClick(date)}
                className={`
                    h-16 border-b border-r border-border p-2 transition-all duration-200 relative
                    ${
                      isCurrentMonth
                        ? "cursor-pointer hover:bg-blue-light hover:shadow-sm"
                        : "text-gray-300 bg-muted cursor-default"
                    }
                    ${isSelected ? "bg-primary/10 ring-2 ring-primary/30" : ""}
                    ${isToday ? "bg-highlight/5" : ""}
                  `}
              >
                <div className="flex flex-col h-full">
                  <div
                    className={`
                        text-lg font-semibold mb-1
                        ${
                          isToday
                            ? "text-primary"
                            : isCurrentMonth
                            ? "text-text-main"
                            : "text-gray-300"
                        }
                      `}
                  >
                    {isCurrentMonth ? (
                      <span
                        className={`
                            ${
                              isToday
                                ? "bg-primary text-secondary rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold"
                                : ""
                            }
                          `}
                      >
                        {getDate(date)}
                      </span>
                    ) : (
                      ""
                    )}
                  </div>

                  {isCurrentMonth && meetingsForDay.length > 0 && (
                    <div className="mt-auto">
                      <div className="flex items-center justify-center gap-1 bg-info/10 rounded-lg px-2 py-1">
                        <CalendarClock className="w-3 h-3 text-info" />
                        <span className="text-xs font-medium text-info leading-none">
                          {meetingsForDay.length}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {selectedDate && (
        <DailyMeetingsSidebar
          date={selectedDate}
          meetings={selectedMeetings}
          onClose={closeSidebar}
          refreshMeetings={refetch} 
        />
      )}
    </div>
  );
};

export default Calendar;
